import React, { useState } from "react";
import {
  HiStar,
  HiOutlineStar,
  HiTrash,
  HiReply,
  HiDotsVertical,
  HiOutlineSearch,
  HiCheck,
  HiX,
  HiRefresh,
  HiMailOpen,
  HiMail,
  HiPhone,
  HiOfficeBuilding,
  HiOutlineDocumentText,
  HiExclamationCircle,
} from "react-icons/hi";
import { Modal, ModalHeader, ModalBody, Button } from "flowbite-react";
import {
  deleteContact,
  bulkDeleteContacts,
  deleteDeveloperContact,
  bulkDeleteDeveloperContacts,
} from "../../api/adminService";

const EmailInbox = ({
  title,
  emails,
  setEmails,
  onReply,
  onUpdateEmail,
  emailType,
  // Pagination props
  currentPage = 1,
  setCurrentPage,
  pageSize = 20,
  setPageSize,
  totalEmails = 0,
  totalPages = 0,
  isServerSidePagination = false,
}) => {
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [activeEmail, setActiveEmail] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [emailsToDelete, setEmailsToDelete] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);

  const toggleSelectEmail = (id) => {
    if (selectedEmails.includes(id)) {
      setSelectedEmails(selectedEmails.filter((emailId) => emailId !== id));
    } else {
      setSelectedEmails([...selectedEmails, id]);
    }
  };

  const toggleSelectAll = () => {
    if (selectedEmails.length === filteredEmails.length) {
      setSelectedEmails([]);
    } else {
      setSelectedEmails(filteredEmails.map((email) => email.id));
    }
  };

  const markAsRead = (ids) => {
    if (onUpdateEmail) {
      ids.forEach((id) => {
        const email = emails.find((e) => e.id === id);
        if (email && !email.read) {
          onUpdateEmail(email._id || email.id, { read: true });
        }
      });
    } else {
      setEmails(
        emails.map((email) =>
          ids.includes(email.id) ? { ...email, read: true } : email,
        ),
      );
    }
    setSelectedEmails([]);
  };

  const markAsUnread = (ids) => {
    if (onUpdateEmail) {
      ids.forEach((id) => {
        const email = emails.find((e) => e.id === id);
        if (email && email.read) {
          onUpdateEmail(email._id || email.id, { read: false });
        }
      });
    } else {
      setEmails(
        emails.map((email) =>
          ids.includes(email.id) ? { ...email, read: false } : email,
        ),
      );
    }
    setSelectedEmails([]);
  };

  const toggleImportance = (id) => {
    const email = emails.find((e) => e.id === id);
    if (!email) return;

    if (onUpdateEmail) {
      onUpdateEmail(email._id || email.id, { important: !email.important });
    } else {
      setEmails(
        emails.map((email) =>
          email.id === id ? { ...email, important: !email.important } : email,
        ),
      );
    }
  };

  const showDeleteConfirmation = (ids) => {
    setEmailsToDelete(ids);
    setShowDeleteModal(true);
  };

  const determineEmailType = () => {
    if (emailType) {
      return emailType;
    }

    const titleLower = title?.toLowerCase() || "";
    if (
      titleLower.includes("subscription") ||
      titleLower.includes("developer")
    ) {
      return "subscription";
    } else if (
      titleLower.includes("inquiry") ||
      titleLower.includes("contact")
    ) {
      return "inquiry";
    }

    console.warn("Email type not specified, defaulting to inquiry");
    return "inquiry";
  };

  const confirmDelete = async () => {
    if (emailsToDelete.length === 0) return;

    const effectiveEmailType = determineEmailType();

    try {
      setIsDeleting(true);

      if (emailsToDelete.length === 1) {
        const emailId = emailsToDelete[0];
        const email = emails.find((e) => e.id === emailId);
        if (!email) {
          console.error("Email not found with id:", emailId);
          return;
        }

        const mongoId = String(email._id);

        if (!mongoId) {
          console.error("Cannot delete email: Missing _id property", email);
          return;
        }

        let success = false;

        if (effectiveEmailType === "subscription") {
          try {
            const response = await deleteDeveloperContact(mongoId);
            success = response && response.success;
          } catch (error) {
            console.error("Error deleting developer contact:", error);
            throw error;
          }
        } else {
          try {
            const response = await deleteContact(mongoId);
            success = response && response.success;
          } catch (error) {
            console.error("Error deleting contact:", error);
            throw error;
          }
        }

        if (!success) {
          throw new Error("Failed to delete message");
        }
      } else {
        const mongoIds = [];

        for (const localId of emailsToDelete) {
          const email = emails.find((e) => e.id === localId);
          if (email && email._id) {
            mongoIds.push(String(email._id));
          }
        }

        if (mongoIds.length === 0) {
          console.error("Cannot delete emails: No valid _id properties found");
          return;
        }

        let success = false;

        if (effectiveEmailType === "subscription") {
          try {
            const response = await bulkDeleteDeveloperContacts(mongoIds);
            success = response && response.success;
          } catch (error) {
            console.error("Error bulk deleting developer contacts:", error);
            throw error;
          }
        } else {
          try {
            const response = await bulkDeleteContacts(mongoIds);
            success = response && response.success;
          } catch (error) {
            console.error("Error bulk deleting contacts:", error);
            throw error;
          }
        }

        if (!success) {
          throw new Error("Failed to delete messages");
        }
      }

      setEmails((prevEmails) =>
        prevEmails.filter((email) => !emailsToDelete.includes(email.id)),
      );

      setSelectedEmails([]);

      if (activeEmail && emailsToDelete.includes(activeEmail.id)) {
        setActiveEmail(null);
      }
    } catch (error) {
      console.error("Error deleting emails:", error);
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setEmailsToDelete([]);
    }
  };

  const filteredEmails = emails.filter(
    (email) =>
      email.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
      email.from.toLowerCase().includes(searchQuery.toLowerCase()) ||
      email.message.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleViewEmail = (email) => {
    if (!email.read) {
      markAsRead([email.id]);
    }
    setActiveEmail(email);
  };

  return (
    <div className="flex flex-col bg-[#161616] h-[calc(100vh-130px)] max-h-[800px] min-h-[600px] overflow-hidden">
      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        size="md"
        popup
        position="center"
      >
        <ModalHeader className="bg-[#1A1A1A] border-b border-white/5" />
        <ModalBody className="bg-[#1A1A1A] text-white p-8">
          <div className="text-center">
            <HiExclamationCircle className="mx-auto mb-4 h-14 w-14 text-red-500" />
            <h3 className="mb-5 text-lg font-bold tracking-tight">
              {emailsToDelete.length > 1
                ? `Delete ${emailsToDelete.length} messages permanently?`
                : "Delete this message permanently?"}
            </h3>
            <div className="flex justify-center gap-4">
              <button
                className="px-6 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-full font-bold transition-all disabled:opacity-50"
                onClick={confirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Yes, Delete"}
              </button>
              <button
                className="px-6 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-full font-bold transition-all"
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeleting}
              >
                Cancel
              </button>
            </div>
          </div>
        </ModalBody>
      </Modal>

      {/* Top Header Region */}
      <div className="mb-5 px-2 md:px-0 flex flex-col gap-3 md:flex-row md:justify-between md:items-center">
        <h1 className="text-xl sm:text-2xl font-black text-white tracking-tighter uppercase">
          {title}
        </h1>
        <div className="flex items-center justify-between md:justify-end gap-3 sm:gap-6 w-full md:w-auto">
          <button
            onClick={() => {
              setActiveEmail(null);
            }}
            className="text-white hover:text-[#BDE958] transition-all p-2 rounded-full hover:bg-white/5"
            title="Refresh"
          >
            <HiRefresh className="h-5 w-5" />
          </button>
          <div className="relative group w-full md:min-w-[300px]">
            <HiOutlineSearch className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-500 h-4 w-4 group-focus-within:text-[#BDE958] transition-colors" />
            <input
              type="text"
              placeholder="Search by Email"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-6 py-2.5 bg-[#D9D9D9] hover:bg-white focus:bg-white border-none rounded-full text-[#161616] placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#BDE958] transition-all font-bold text-xs tracking-tight shadow-inner"
            />
          </div>
        </div>
      </div>

      {/* Main Content Beige Card */}
      <div className="flex-1 bg-[#E8ECDA] rounded-3xl flex flex-col overflow-hidden shadow-2xl border border-white/5 mx-0 sm:mx-2 md:mx-0">
        {/* Toolbar Section */}
        <div className="px-4 sm:px-8 py-3.5 flex items-center justify-between border-b border-black/[0.04]">
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              checked={
                selectedEmails.length === filteredEmails.length &&
                filteredEmails.length > 0
              }
              onChange={toggleSelectAll}
              className="h-4 w-4 rounded border-[#C8CFBD] text-[#434A38] focus:ring-[#BDE958] bg-[#EEF2E8] shadow-sm cursor-pointer"
            />
            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-[#5C6353]">
              {selectedEmails.length > 0
                ? `${selectedEmails.length} Selected`
                : "Select all"}
            </span>
          </div>

          <div className="flex items-center space-x-2">
            {selectedEmails.length > 0 && (
              <div className="flex items-center space-x-1 p-1 bg-[#D9E1CC] rounded-full shadow-inner">
                <button
                  onClick={() => markAsRead(selectedEmails)}
                  className="p-1.5 rounded-full text-[#5C6353] hover:bg-white hover:text-[#161616] transition-all"
                  title="Mark as read"
                >
                  <HiMailOpen className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => markAsUnread(selectedEmails)}
                  className="p-1.5 rounded-full text-[#5C6353] hover:bg-white hover:text-[#161616] transition-all"
                  title="Mark as unread"
                >
                  <HiMail className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => showDeleteConfirmation(selectedEmails)}
                  className="p-1.5 rounded-full text-[#5C6353] hover:bg-red-500 hover:text-white transition-all"
                  title="Delete"
                >
                  <HiTrash className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Split View Container */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Sidebar: Email List */}
          <div
            className={`${activeEmail ? "hidden md:flex md:w-1/3" : "w-full"} flex flex-col border-r border-[#D5DABF] bg-black/[0.02] overflow-hidden`}
          >
            <div className="flex-1 overflow-y-auto custom-scrollbar divide-y divide-[#D5DABF]">
              {filteredEmails.length > 0 ? (
                filteredEmails.map((email) => (
                  <div
                    key={email.id}
                    onClick={() => handleViewEmail(email)}
                    className={`relative cursor-pointer transition-all duration-300 group ${
                      activeEmail?.id === email.id
                        ? "bg-[#DEE3D0] shadow-inner"
                        : "bg-[#E8ECDA] hover:bg-[#DEE3D0]/60"
                    }`}
                  >
                    {/* Active/Unread Indicators */}
                    {activeEmail?.id === email.id && (
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#434A38] rounded-r z-10" />
                    )}
                    {!email.read && (
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500 z-20" />
                    )}

                    <div className="flex items-start p-5">
                      <div className="h-full flex flex-col items-center mr-3.5 space-y-3.5 pt-1">
                        <input
                          type="checkbox"
                          checked={selectedEmails.includes(email.id)}
                          onChange={(e) => {
                            e.stopPropagation();
                            toggleSelectEmail(email.id);
                          }}
                          className="h-3.5 w-3.5 rounded border-[#C8CFBD] text-[#434A38] focus:ring-[#BDE958] bg-[#EEF2E8] shadow-sm cursor-pointer"
                        />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleImportance(email.id);
                          }}
                          className="transition-transform hover:scale-125"
                        >
                          {email.important ? (
                            <HiStar className="h-4 w-4 text-[#BDE958]" />
                          ) : (
                            <HiOutlineStar className="h-4 w-4 text-[#8B9382]" />
                          )}
                        </button>
                      </div>

                      <div className="flex-1 min-w-0 pr-1">
                        <div className="flex justify-between items-start mb-0.5">
                          <span
                            className={`text-[11px] font-black tracking-tight flex items-center truncate ${email.read ? "text-[#5C6353]" : "text-[#161616]"}`}
                          >
                            {email.from}
                            {!email.read && (
                              <span className="ml-1.5 w-1 h-1 rounded-full bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.6)]"></span>
                            )}
                          </span>
                          <span className="text-[8px] font-bold text-[#8B9382] whitespace-nowrap ml-2 opacity-60">
                            {email.timestamp}
                          </span>
                        </div>
                        <h4
                          className={`text-xs tracking-tight mb-0.5 truncate ${email.read ? "text-[#5C6353] font-medium" : "text-[#161616] font-extrabold"}`}
                        >
                          {email.subject}
                        </h4>
                        <p className="text-[10px] text-[#5C6353]/70 truncate font-medium">
                          {email.message}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="flex flex-col items-center justify-center h-64 opacity-20">
                  <HiMailOpen className="h-12 w-12 text-[#5C6353]" />
                  <p className="mt-4 text-[#5C6353] font-black text-[10px] uppercase tracking-widest">
                    No messages
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Right Section: Email Content */}
          <div
            className={`${activeEmail ? "flex w-full md:w-2/3" : "hidden md:flex md:w-2/3"} flex-col bg-black/[0.02] overflow-hidden`}
          >
            {activeEmail ? (
              <div className="flex-1 flex flex-col overflow-hidden">
                {/* Scrollable Email Content */}
                <div className="flex-1 overflow-y-auto custom-scrollbar p-6 lg:p-8">
                  {/* Content Header Card */}
                  <div className="bg-[#E8ECDA] rounded-2xl p-6 mb-6 shadow-lg border-transparent relative group">
                    {/* Action Icons Panel */}
                    <div className="absolute right-6 top-6 flex items-center space-x-2">
                      <button
                        onClick={() => toggleImportance(activeEmail.id)}
                        className="p-2 rounded-full text-[#8B9382] hover:bg-[#EEF2E8] hover:text-[#434A38] transition-all border border-transparent hover:border-[#D9E1CC]"
                      >
                        {activeEmail.important ? (
                          <HiStar className="h-4 w-4 text-[#BDE958]" />
                        ) : (
                          <HiOutlineStar className="h-4 w-4" />
                        )}
                      </button>
                      <button
                        onClick={() => showDeleteConfirmation([activeEmail.id])}
                        className="p-2 rounded-full text-[#8B9382] hover:bg-red-50 hover:text-white transition-all border border-transparent hover:border-red-200"
                      >
                        <HiTrash className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setActiveEmail(null)}
                        className="md:hidden p-2 rounded-full text-[#8B9382] hover:bg-[#EEF2E8] transition-all"
                      >
                        <HiDotsVertical className="h-4 w-4" />
                      </button>
                    </div>

                    {/* sender avatar and details */}
                    <div className="flex items-start space-x-5">
                      <div className="h-14 w-14 rounded-full bg-[#434A38] flex items-center justify-center text-white text-xl font-black shadow-md ring-3 ring-[#E8ECDA] shrink-0">
                        {activeEmail.from
                          ? activeEmail.from.charAt(0).toUpperCase()
                          : "N"}
                      </div>
                      <div className="min-w-0 flex-1 pt-0.5 pr-20">
                        <h2 className="text-xl font-black text-[#161616] tracking-tighter mb-1 leading-tight">
                          {activeEmail.subject}
                        </h2>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                          <span className="text-[11px] font-black text-[#434A38]">
                            From: {activeEmail.from}
                          </span>
                          <span className="text-[11px] font-bold text-[#8B9382] tracking-normal">
                            &lt;{activeEmail.email}&gt;
                          </span>
                        </div>
                        <div className="mt-4 flex flex-wrap gap-3 pt-4 border-t border-black/[0.04]">
                          {activeEmail.contactNumber && (
                            <div className="flex items-center text-[10px] font-bold text-[#5C6353] bg-[#EEF2E8] px-2.5 py-1 rounded-lg border border-black/[0.03]">
                              <HiPhone className="h-3 w-3 text-[#8B9382] mr-1.5" />
                              {activeEmail.contactNumber}
                            </div>
                          )}
                          {activeEmail.organisationName && (
                            <div className="flex items-center text-[10px] font-bold text-[#5C6353] bg-[#EEF2E8] px-2.5 py-1 rounded-lg border border-black/[0.03]">
                              <HiOfficeBuilding className="h-3 w-3 text-[#8B9382] mr-1.5" />
                              {activeEmail.organisationName}
                            </div>
                          )}
                          <div className="flex items-center text-[10px] font-bold text-[#8B9382] ml-auto">
                            {activeEmail.timestamp}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Message Body Area */}
                  <div className="flex flex-col">
                    <div className="flex items-center mb-4 px-2">
                      <span className="text-[9px] font-black uppercase tracking-[0.2em] text-[#5C6353] filter brightness-75">
                        Message Body
                      </span>
                      <div className="ml-3 flex-1 h-px bg-black/[0.04]"></div>
                    </div>
                    <div className="bg-white/40 rounded-2xl p-8 shadow-inner border border-white/40">
                      <p className="whitespace-pre-line text-[13px] leading-relaxed text-[#2C3127] font-medium selection:bg-[#BDE958] selection:text-[#161616]">
                        {activeEmail.message}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Reply Action Footer - Fixed at bottom */}
                <div className="shrink-0 px-6 lg:px-8 py-4 border-t border-[#D5DABF] bg-[#E8ECDA]/50 backdrop-blur-sm">
                  <button
                    onClick={() => onReply(activeEmail)}
                    className="group relative flex items-center px-8 py-3 bg-[#BDE958] text-[#161616] rounded-xl font-black text-[10px] uppercase tracking-widest shadow-lg hover:shadow-[#BDE958]/20 hover:-translate-y-0.5 transition-all duration-300"
                  >
                    <HiReply className="mr-2.5 h-4 w-4 transform group-hover:-rotate-12 transition-transform" />
                    Reply to Message
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center p-10 text-center opacity-40">
                <div className="h-20 w-20 bg-[#D9E1CC] rounded-full flex items-center justify-center mb-5 shadow-inner">
                  <HiMailOpen className="h-8 w-8 text-[#5C6353]" />
                </div>
                <h3 className="text-sm font-black text-[#5C6353] uppercase tracking-widest mb-1.5">
                  Select a message
                </h3>
                <p className="text-xs font-bold text-[#8B9382] max-w-xs leading-relaxed">
                  Click on an email from the list to view its contents
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Unified Pagination Footer */}
        <div className="px-8 py-5 bg-white/[0.15] border-t border-black/[0.04] backdrop-blur-sm">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex items-center space-x-6">
              {setPageSize && (
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-[#8C9086] font-medium">
                    Show:
                  </span>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="bg-[#D5DABF]/50 border-none rounded-lg text-sm font-bold text-[#3A3A3A] py-1 pl-2 pr-6 focus:ring-0 cursor-pointer hover:bg-[#D5DABF] transition-colors"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              )}
            </div>

            {isServerSidePagination && totalEmails > 0 && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 rounded-lg bg-white/40 border border-white/60 text-[9px] font-black uppercase tracking-widest text-[#5C6353] hover:bg-white hover:text-[#161616] disabled:opacity-40 transition-all shadow-sm"
                >
                  Prev
                </button>

                <div className="flex items-center space-x-1 px-1">
                  {(() => {
                    const getPageNumbers = () => {
                      const pages = [];
                      const totalPageCount = totalPages;

                      if (totalPageCount <= 7) {
                        for (let i = 1; i <= totalPageCount; i++) {
                          pages.push(i);
                        }
                      } else {
                        pages.push(1);
                        if (currentPage <= 4) {
                          for (let i = 2; i <= 5; i++) {
                            pages.push(i);
                          }
                          if (totalPageCount > 6) {
                            pages.push("...");
                          }
                        } else if (currentPage >= totalPageCount - 3) {
                          if (totalPageCount > 6) {
                            pages.push("...");
                          }
                          for (
                            let i = totalPageCount - 4;
                            i <= totalPageCount - 1;
                            i++
                          ) {
                            if (i > 1) pages.push(i);
                          }
                        } else {
                          if (currentPage > 3) {
                            pages.push("...");
                          }
                          for (
                            let i = currentPage - 1;
                            i <= currentPage + 1;
                            i++
                          ) {
                            if (i > 1 && i < totalPageCount) pages.push(i);
                          }
                          if (currentPage < totalPageCount - 2) {
                            pages.push("...");
                          }
                        }
                        if (totalPageCount > 1) {
                          pages.push(totalPageCount);
                        }
                      }
                      return pages;
                    };

                    return getPageNumbers().map((pageNum, idx) => (
                      <button
                        key={idx}
                        onClick={() =>
                          typeof pageNum === "number" && setCurrentPage(pageNum)
                        }
                        disabled={pageNum === "..."}
                        className={`w-7 h-7 rounded-lg flex items-center justify-center text-[9px] font-black transition-all ${
                          pageNum === currentPage
                            ? "bg-[#161616] text-[#BDE958] shadow-lg"
                            : pageNum === "..."
                              ? "text-[#8B9382] cursor-default"
                              : "text-[#5C6353] hover:bg-white/60"
                        }`}
                      >
                        {pageNum}
                      </button>
                    ));
                  })()}
                </div>

                <button
                  onClick={() =>
                    setCurrentPage(Math.min(totalPages, currentPage + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 rounded-lg bg-white/40 border border-white/60 text-[9px] font-black uppercase tracking-widest text-[#5C6353] hover:bg-white hover:text-[#161616] disabled:opacity-40 transition-all shadow-sm"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailInbox;
